import { Grid2, Typography, Box, Link, Snackbar, Alert, Divider, Container, useTheme, useMediaQuery, Button, IconButton, Drawer } from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyPlanAdListItem, Currencies, CURRENCY_SYMBOLS, PlanAdListItem, QuickOrderInfo, QuickOrderListItem, QuickOrderProduct, ResourceListItem } from '../../../types';
import { QuickOrderCard, CreateQuickOrderCard, QuickOrderCardVendorInvitation } from './QuickOrderCard';
import { ResourcesList } from './ResourcesList';
import { useQuickOrderCreateMutation } from '../../../services/quickOrders';
import { useAppSelector } from '../../../hooks';
import { Trans, useTranslation } from 'react-i18next';
import { PromoteCompanyToggleCard } from './PromoteCompanyCard';
import { usePlanAdsDeleteMutation } from '../../../services/planAds';
import useAuth from '../../../components/Auth';
import { PlanAdCard } from './PlanAdCard';
import { ProductDetailDialogFullScreen } from '../../../components/ProductDetailDialogFullScreen';
import { useShippingRegionContext } from '../../../contexts/ShippingRegionContext';
import { RegionSelector } from '../../../components/RegionSelector';
import { selectMyCompany } from '../../accountSlice';
import { calculateOrderTotals } from '../../../common/util';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';

interface PlanViewResourcesTabContentProps {
	resources: ResourceListItem[];
	planShoppingLists: QuickOrderInfo[];
	companyShoppingList: QuickOrderListItem | null;
	planAds: PlanAdListItem[];
	companyPlanAd: CompanyPlanAdListItem | null;
	planId: number;
	handleFavouriteClick: () => void;
	isVendorsEnabled: boolean;
	isAuthorVerified: boolean;
}

type OrderProductProps = Pick<QuickOrderProduct, 'id' | 'name' | 'productId' | 'quantity' | 'pricePerUnit'>;

type ShoppingCartItem = Pick<QuickOrderProduct, 'id' | 'name' | 'quickOrderId' | 'productId' | 'quantity' | 'pricePerUnit'> & {
	quickOrderId: number;
	region: string;
	planId: number;
}

export const PlanViewResourcesTabContent = ({ resources, planShoppingLists, companyShoppingList, planAds, companyPlanAd, planId, handleFavouriteClick, isVendorsEnabled, isAuthorVerified }: PlanViewResourcesTabContentProps) => {
	const theme = useTheme();
	const {userProfile} = useAuth();
	const companyProfile = useAppSelector(selectMyCompany);
	const [quickOrderCreateMutation, { isSuccess: isCreateSuccess, data: createData, error: quickOrderCreateError, reset: quickOrderCreateReset }] = useQuickOrderCreateMutation();
	const [planAdsDeleteMutation] = usePlanAdsDeleteMutation();
	const { selectedRegion } = useShippingRegionContext();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [productDialogProduct, setProductDialogProduct] = useState<QuickOrderProduct & { disabled?: boolean, quickOrderId: number } | null>(null);
	const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
	const companyUnpublishedLists = useMemo(() => companyShoppingList?.publishedAt === null ? [companyShoppingList] : [], [companyShoppingList]);
	const companyUnpublishedAds = useMemo(() => companyPlanAd && !companyPlanAd.isPublished ? [companyPlanAd] : [], [companyPlanAd]);
	// @deprecated use shoppingCartItems instead
	const [selectedProductQuantities, setSelectedProductQuantities] = useState<Map<number, string>>(new Map());
	const [selectedQuickOrders, setSelectedQuickOrders] = useState<Set<number>>(new Set());
	const [shoppingCartItems, setShoppingCartItems] = useState<ShoppingCartItem[]>([]);
	
	const isShoppingCartOpen = useMemo(() => shoppingCartItems.length > 0, [shoppingCartItems]);
	
	const shoppingCartTotal = useMemo(() => {
		const {total} = calculateOrderTotals({products: shoppingCartItems, additionalFees: []});
		return total;
	}, [shoppingCartItems]);
	
	// Create a lookup map for resources by their ID
	const resourcesById = new Map<number, ResourceListItem>(
		resources.map((resource: ResourceListItem) => [resource.id, resource])
	);
	
	// Convert quickOrders into our QuickOrderCard format
	const allShoppingLists = useMemo(() => [...companyUnpublishedLists, ...planShoppingLists].map((quickOrder) => {
		return {
			...quickOrder,
			title: `${t('planViewQuickOrderCard.cartBy', { vendorName: quickOrder.vendor.name })}`,
			image: quickOrder.vendor.imgUrl,
		};
	}), [companyUnpublishedLists, planShoppingLists]);
	const currency = useMemo(() => allShoppingLists[0]?.currency ?? null, [allShoppingLists]);
	
	const allPlanAds = useMemo(() => [...planAds, ...companyUnpublishedAds] as (CompanyPlanAdListItem | PlanAdListItem)[], [planAds, companyUnpublishedAds]);
	
	useEffect(() => {
		if (isCreateSuccess && createData) {
			navigate(`/build-kits/${createData.id}`);
		}
	}, [isCreateSuccess, createData]);
	
	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth'
		});
	}, []);
	
	useEffect(() => {
		const products = allShoppingLists.flatMap(list => list.products.map(product => {
			const resource = resourcesById.get(product.resourceId);
			return {
				id: product.id,
				quantity: (resource && (resource.isTool || resource.isOptional)) ? '0' : product.quantity
			};
		}));

		const storedShoppingCartItems = (localStorage.getItem('shoppingCartItems') && JSON.parse(localStorage.getItem('shoppingCartItems') as string)) || [];
		const relevantItems = storedShoppingCartItems.filter((item: ShoppingCartItem) => item.region === selectedRegion && item.planId === planId);
		const recipeQuantities = new Map(products.map(product => [product.id, product.quantity]));
		if (relevantItems.length > 0) {
			// reset stored quantities on selected shopping lists
			setShoppingCartItems(relevantItems);
			setSelectedQuickOrders(new Set(relevantItems.map((item: ShoppingCartItem) => item.quickOrderId)));
			// override recipe quantities with stored quantities
			setSelectedProductQuantities(new Map([...Array.from(recipeQuantities.entries()), ...relevantItems.map((item: ShoppingCartItem) => [item.id, item.quantity])]));
		} else {
			// use recipe quantities
			setShoppingCartItems([]);
			setSelectedProductQuantities(recipeQuantities);
			setSelectedQuickOrders(new Set());
		}
	}, [allShoppingLists, selectedRegion]);

	const handleProductQuantityChange = (quickOrderId: number, product: QuickOrderProduct, newQuantity: number) => {
		const roundedQuantity = Math.round(newQuantity * 100) / 100;
		setSelectedProductQuantities(prev => {
			const newMap = new Map(prev);
			newMap.set(product.id, roundedQuantity.toString());
			return newMap;
		});
		if (!selectedQuickOrders.has(quickOrderId)) {
			return;
		}
		let newShoppingCartItems = [];
		if (roundedQuantity === 0) {
			newShoppingCartItems = shoppingCartItems.filter(item => item.id !== product.id);
		} else {
			newShoppingCartItems = shoppingCartItems.map(item => item.id === product.id ? {
				...item,
				quantity: roundedQuantity.toString()
			} : item);
			if (!shoppingCartItems.some(item => item.id === product.id)) {
				const newItem: ShoppingCartItem = {
					...product,
					quickOrderId,
					quantity: roundedQuantity.toString(),
					region: selectedRegion as string,
					planId: planId,
				};
				newShoppingCartItems = [...shoppingCartItems, newItem];
			}
		}
		setShoppingCartItems(newShoppingCartItems);
		localStorage.setItem('shoppingCartItems', JSON.stringify(newShoppingCartItems));
	};

	const handleEdit = (quickOrderId: number) => {
		navigate(`/build-kits/${quickOrderId}`);
	};

	const handleCreate = () => {
		quickOrderCreateMutation({planId: planId});
	};

	const handleAddToCart = (quickOrderId: number, products: OrderProductProps[]) => {
		let newShoppingCartItems = [];
		if (selectedQuickOrders.has(quickOrderId)) {
			setSelectedQuickOrders(prev => {
				const newSet = new Set(prev);
				newSet.delete(quickOrderId);
				return newSet;
			});
			newShoppingCartItems = shoppingCartItems.filter(item => item.quickOrderId !== quickOrderId);
		} else {
			newShoppingCartItems = [...shoppingCartItems, ...products.map(product => ({
				id: product.id,
				name: product.name,
				productId: product.productId,
				quantity: product.quantity,
				pricePerUnit: product.pricePerUnit,
				quickOrderId,
				region: selectedRegion as string,
				planId: planId,
			})).filter(product => Number(product.quantity) > 0)];
			setSelectedQuickOrders(prev => {
				const newSet = new Set(prev);
				newSet.add(quickOrderId);
				return newSet;
			});
		}
		setShoppingCartItems(newShoppingCartItems);
		localStorage.setItem('shoppingCartItems', JSON.stringify(newShoppingCartItems));
	};

	const handleMoveToCheckout = async () => {
		localStorage.removeItem('paymentIntent'); // remove previous payment intent in case user initiates a new checkout
		navigate('/checkout');
	};

	const shouldShowCreateQuickOrderCard = companyProfile?.roleVendor && !companyShoppingList;
	const shouldShowCreatePlanAd = companyProfile?.roleVendor && !allPlanAds.length && !companyShoppingList;
	const shouldShowQuickOrderCardVendorInvitation = !companyProfile?.roleVendor && allShoppingLists.length > 0 && isVendorsEnabled;

	const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
	const showNoShoppingListsNotice = !allShoppingLists.length;

	return (
		<Container sx={{pb: 8}}>
			<Box sx={{mb: isMdUp && !showNoShoppingListsNotice ? -7 : 3}}>
				<Box display='flex' flexDirection={{xs: 'column', md: 'row'}} gap={2} justifyContent='flex-end'>
					{showNoShoppingListsNotice && <Box sx={{flex: 1}}>
						<Typography variant="body2">{<Trans i18nKey='planView.noShoppingListsDescription' components={{br: <br />, Link: <Link onClick={handleFavouriteClick} />}} />}</Typography>
					</Box>}
					
					{/* Region Selector */}
					<Box display='flex'>
						<RegionSelector />
					</Box>
				</Box>
			</Box>
			<Grid2 container spacing={2}>
				{allShoppingLists.length > 0 && <Box display='flex' flexDirection='column' gap={3}>
					{allShoppingLists.map((list) => (
						<Box key={list.id} sx={{filter: selectedRegion ? 'none' : 'opacity(0.5)'}}>
							<QuickOrderCard
								key={list.id} 
								list={list}
								resources={resources}
								onProductQuantityChange={(product, newQuantity) => handleProductQuantityChange(list.id, product, newQuantity)}
								onAddToCart={(products) => handleAddToCart(list.id, products)}
								onEdit={() => handleEdit(list.id)}
								onItemClick={(product) => {
									setProductDialogProduct({...product, disabled: !list.shippingRegions.includes(selectedRegion as string) && !product.digitalDelivery, quickOrderId: list.id});
									setIsProductDialogOpen(true);
								}}
								isAuthor={list.vendorId === userProfile?.companyId}
								isDraft={list.publishedAt === null}
								selectedProductQuantities={selectedProductQuantities}
								selectedQuickOrders={selectedQuickOrders}
							/>
						</Box>
					))}
					{shouldShowQuickOrderCardVendorInvitation && <Box>
						<QuickOrderCardVendorInvitation />
					</Box>}
				</Box>}
				<Grid2>
					<ResourcesList items={resources} isUnverified={!isAuthorVerified} defaultCollapsed={Boolean(allShoppingLists.length > 0 || allPlanAds.length > 0)} />
				</Grid2>
				<Grid2 size={{xs: 12, md: 6}} spacing={2}>
					<Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
						{Boolean(shouldShowCreateQuickOrderCard) && <CreateQuickOrderCard onCreate={handleCreate} />}
						{Boolean(shouldShowCreatePlanAd) && <PromoteCompanyToggleCard isPromoted={Boolean(companyPlanAd)} />}
					</Box>
				</Grid2>
			</Grid2>
			{allPlanAds.length > 0 && <>
				<Divider sx={{ my: 2 }} />
				<Typography variant="h6" display='flex' alignItems='center' mt={3} mb={2}>
					{t('planView.askPartner')}
				</Typography>
			</>}
			<Grid2 container spacing={2}>
				{allPlanAds?.map(ad => (
					<Grid2 size={{xs: 12, md: 'auto'}} key={ad.id}>
						<PlanAdCard 
							planId={planId}
							ad={ad}
							isAuthor={ad.companyId === companyProfile?.id}
							isUnpublished={ad.id === companyPlanAd?.id && !companyPlanAd.isPublished}
							onDelete={() => planAdsDeleteMutation({id: ad.id})}
						/>
					</Grid2>
				))}
				<ProductDetailDialogFullScreen
					open={isProductDialogOpen}
					onClose={() => setIsProductDialogOpen(false)}
					product={productDialogProduct}
					selectedQuantity={
						productDialogProduct
							? selectedProductQuantities.get(productDialogProduct.id) ?? '0'
							: '0'
					}
					onQuantityChange={(productId, newQuantity) => {
						if (!productDialogProduct) {
							return;
						}
						handleProductQuantityChange(productDialogProduct.quickOrderId, productDialogProduct, newQuantity);
					}}
					maxQuantity={productDialogProduct?.digitalDelivery ? 1 : undefined}
					disabled={productDialogProduct?.disabled}
				/>
				<Snackbar open={Boolean(quickOrderCreateError)} autoHideDuration={6000} onClose={quickOrderCreateReset}>
					<Alert severity='error'>{(quickOrderCreateError as any)?.data?.message}</Alert>
				</Snackbar>
			</Grid2>
			<Drawer
				anchor='bottom'
				variant='persistent'
				open={isShoppingCartOpen}
				onClose={() => setShoppingCartItems([])}
			>
				<Box display='flex' flexDirection='row' alignItems='center' justifyContent='flex-end' gap={2} p={2} pb={3} px={4}>
					<Box display='flex' flexDirection='column' gap={1} alignItems='flex-end'>
						<Typography variant="body2" color="text.secondary" textAlign='center' fontSize={16}>
							{t('planViewCheckout.itemsSelected', {count: shoppingCartItems.length})}. {t('planViewCheckout.total')}: <span style={{color: theme.palette.primary.main}}>{CURRENCY_SYMBOLS[currency as Currencies]}{shoppingCartTotal.toFixed(2)}</span>
						</Typography>
					</Box>
					<div>
						{isMdUp ? <Button size='large' variant='contained' color='primary' onClick={() => handleMoveToCheckout()} startIcon={<ShoppingCartCheckoutIcon />}>{t('planViewCheckout.toCheckout')}</Button>
							: <IconButton size='large' sx={{backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText, '&:hover': {backgroundColor: theme.palette.primary.dark}}} onClick={() => handleMoveToCheckout()}>
								<ShoppingCartCheckoutIcon />
							</IconButton>}
					</div>
				</Box>
			</Drawer>
		</Container>
	);
};
